import { useEffect, useMemo, useRef } from 'react';

type Options = Pick<AddEventListenerOptions, 'capture' | 'passive'>;

/**
 * @param eventName The event name (string). Here is a list of
 *                  [common events](https://developer.mozilla.org/en-US/docs/Web/Events).
 * @param handler   A function that will be called whenever `eventName`
 *                  fires on `element`.
 * @param element   An optional element to listen on. Defaults to `global`
 *                  (i.e., `window`).
 * @param options   An object `{ capture?: boolean, passive?: boolean }` to be
 *                  passed to `addEventListener`. For advanced use cases. See
 *                  [MDN](https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener) for details.
 */
function useEventListener<
  K extends keyof (HTMLElementEventMap & DocumentEventMap & WindowEventMap)
>(
  eventName: K,
  handler: (
    event: (HTMLElementEventMap & DocumentEventMap & WindowEventMap)[K]
  ) => void,
  element?: HTMLElement | Document | Window | null,
  options?: Options
) {
  const savedHandler = useRef<any>();

  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  const capture = options?.capture;
  const passive = options?.passive;

  // Rerun the effect only if capture or passive has actually changed
  const memoOptions = useMemo(() => ({ capture, passive }), [capture, passive]);

  useEffect(() => {
    const isSupported = element && element.addEventListener;
    if (!isSupported) return;

    const eventListener = (event) => savedHandler.current(event);
    element.addEventListener(eventName, eventListener, memoOptions);

    return () => {
      element.removeEventListener(eventName, eventListener);
    };
  }, [eventName, element, memoOptions]);
}

export default useEventListener;
